/**
 * Accounts - what the business owes, what it is owed, and the petty-cash book.
 *
 * Everything here reads from tables the rest of the app already writes to:
 * SUPPLIER.Current_Balance is moved by purchases, CUSTOMER.Current_Balance by
 * credit sales, and CASH_MOVEMENT is the drawer. There is no separate ledger,
 * because a restaurant this size does not keep one and a set of tables nobody
 * fills in is worse than none.
 *
 * Recording a cash payment here also moves the drawer, so the figure a manager
 * counts at close still reconciles.
 */

import { useState, useCallback } from 'react';
import {
    FiArrowUpRight, FiArrowDownLeft, FiDollarSign, FiTruck, FiUsers,
    FiClock, FiAlertTriangle
} from 'react-icons/fi';

import { accountsApi } from '../services/endpoints';
import useAsync, { useAction } from '../hooks/useAsync';
import DataTable from '../components/ui/DataTable';
import Modal from '../components/ui/Modal';
import { PageHeader, StatCard, Badge } from '../components/ui/Feedback';
import { Field, NumberInput, Input, Checkbox } from '../components/ui/Field';
import { money, formatDate, formatDateTime, formatMobile } from '../utils/format';

const TABS = [
    { key: 'payables', label: 'Suppliers we owe', icon: FiTruck },
    { key: 'receivables', label: 'Customers who owe us', icon: FiUsers },
    { key: 'cash', label: 'Cash book', icon: FiDollarSign }
];

const AccountsPage = () => {
    const [tab, setTab] = useState('payables');
    const [settling, setSettling] = useState(null);   // { kind, row }

    const loadSummary = useCallback(() => accountsApi.summary(), []);
    const { data: summary, refetch: refetchSummary } = useAsync(loadSummary, []);

    const load = useCallback(() => {
        if (tab === 'payables') return accountsApi.payables();
        if (tab === 'receivables') return accountsApi.receivables();
        return accountsApi.cashMovements({});
    }, [tab]);

    const { data: rows, loading, refetch } = useAsync(load, [tab]);

    const refreshAll = () => { refetch(); refetchSummary(); };

    /* ------------------------------- columns ------------------------------ */

    const payableColumns = [
        {
            key: 'Supplier_Name',
            header: 'Supplier',
            render: (row) => (
                <div className="min-w-0">
                    <p className="truncate font-medium text-slate-800">{row.Supplier_Name}</p>
                    <p className="truncate text-xs text-slate-500">
                        {row.Contact_Person || '—'}
                        {row.Mobile_No && ` · ${formatMobile(row.Mobile_No)}`}
                    </p>
                </div>
            )
        },
        { key: 'Invoice_Count', header: 'Unpaid deliveries', align: 'right' },
        {
            key: 'Oldest_Date',
            header: 'Oldest',
            render: (row) => (row.Oldest_Date ? formatDate(row.Oldest_Date) : '—')
        },
        {
            key: 'Days_Outstanding',
            header: 'Age',
            render: (row) => {
                if (row.Days_Outstanding == null) return '—';
                // Past the agreed credit days is the thing worth seeing at a
                // glance; anything inside terms is just information.
                const overdue = row.Credit_Days > 0 && row.Days_Outstanding > row.Credit_Days;
                return overdue
                    ? (
                        <span className="flex items-center gap-1 text-sm font-medium text-red-600">
                            <FiAlertTriangle className="h-3.5 w-3.5" />
                            {row.Days_Outstanding} days
                        </span>
                    )
                    : <span className="text-slate-600">{row.Days_Outstanding} days</span>;
            }
        },
        {
            key: 'Current_Balance',
            header: 'Owed',
            align: 'right',
            render: (row) => (
                <span className="font-semibold text-amber-700">{money(row.Current_Balance)}</span>
            )
        },
        {
            key: 'actions',
            header: '',
            render: (row) => (
                <div className="flex justify-end">
                    <button
                        type="button"
                        className="btn-secondary btn-sm"
                        onClick={(event) => {
                            event.stopPropagation();
                            setSettling({ kind: 'supplier', row });
                        }}
                    >
                        Record payment
                    </button>
                </div>
            )
        }
    ];

    const receivableColumns = [
        {
            key: 'Customer_Name',
            header: 'Customer',
            render: (row) => (
                <div className="min-w-0">
                    <p className="truncate font-medium text-slate-800">{row.Customer_Name}</p>
                    <p className="truncate text-xs text-slate-500">
                        {row.Mobile_No ? formatMobile(row.Mobile_No) : 'No mobile'}
                    </p>
                </div>
            )
        },
        { key: 'Invoice_Count', header: 'Unpaid bills', align: 'right' },
        {
            key: 'Oldest_Date',
            header: 'Oldest',
            render: (row) => (row.Oldest_Date ? formatDate(row.Oldest_Date) : '—')
        },
        {
            key: 'Credit_Limit',
            header: 'Limit',
            align: 'right',
            render: (row) => {
                const limit = Number(row.Credit_Limit) || 0;
                if (limit === 0) return <span className="text-slate-400">none set</span>;

                const over = Number(row.Current_Balance) > limit;
                return (
                    <span className={over ? 'font-medium text-red-600' : 'text-slate-600'}>
                        {money(limit)}
                        {over && <Badge tone="red" className="ml-2">over</Badge>}
                    </span>
                );
            }
        },
        {
            key: 'Current_Balance',
            header: 'Due',
            align: 'right',
            render: (row) => (
                <span className="font-semibold text-blue-700">{money(row.Current_Balance)}</span>
            )
        },
        {
            key: 'actions',
            header: '',
            render: (row) => (
                <div className="flex justify-end">
                    <button
                        type="button"
                        className="btn-secondary btn-sm"
                        onClick={(event) => {
                            event.stopPropagation();
                            setSettling({ kind: 'customer', row });
                        }}
                    >
                        Record receipt
                    </button>
                </div>
            )
        }
    ];

    const cashColumns = [
        {
            key: 'Created_Date_Time',
            header: 'When',
            render: (row) => formatDateTime(row.Created_Date_Time)
        },
        {
            key: 'Movement_Type',
            header: 'Direction',
            render: (row) => (row.Movement_Type === 'IN'
                ? <Badge tone="green" icon={FiArrowDownLeft}>in</Badge>
                : <Badge tone="amber" icon={FiArrowUpRight}>out</Badge>)
        },
        {
            key: 'Reason',
            header: 'Reason',
            render: (row) => (
                <span className="block max-w-[24rem] truncate">{row.Reason || '—'}</span>
            )
        },
        { key: 'Recorded_By', header: 'By' },
        { key: 'Session_Number', header: 'Shift' },
        {
            key: 'Amount',
            header: 'Amount',
            align: 'right',
            render: (row) => (
                <span className={`font-semibold ${row.Movement_Type === 'IN' ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {row.Movement_Type === 'IN' ? '+' : '−'}{money(row.Amount)}
                </span>
            )
        }
    ];

    const columns = tab === 'payables' ? payableColumns
        : tab === 'receivables' ? receivableColumns
            : cashColumns;

    return (
        <div className="page">
            <PageHeader
                title="Accounts"
                subtitle="Money owed, money owing, and the cash book"
            />

            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3">
                <StatCard
                    label="Owed to suppliers"
                    value={`Rs. ${money(summary?.payables?.total)}`}
                    sub={`${summary?.payables?.supplierCount || 0} supplier(s)`}
                    icon={FiTruck}
                    tone={summary?.payables?.total > 0 ? 'amber' : 'slate'}
                    onClick={() => setTab('payables')}
                />
                <StatCard
                    label="Owed by customers"
                    value={`Rs. ${money(summary?.receivables?.total)}`}
                    sub={`${summary?.receivables?.customerCount || 0} customer(s)`}
                    icon={FiUsers}
                    tone={summary?.receivables?.total > 0 ? 'blue' : 'slate'}
                    onClick={() => setTab('receivables')}
                />
                <StatCard
                    label="Petty cash today"
                    value={`Rs. ${money(summary?.pettyCashToday?.net)}`}
                    sub={`in ${money(summary?.pettyCashToday?.in)} · out ${money(summary?.pettyCashToday?.out)}`}
                    icon={FiClock}
                    tone="slate"
                    onClick={() => setTab('cash')}
                />
            </div>

            <div className="flex gap-2 overflow-x-auto scrollbar-none border-b border-slate-200">
                {TABS.map((item) => (
                    <button
                        key={item.key}
                        type="button"
                        onClick={() => setTab(item.key)}
                        className={`-mb-px flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2.5
                                    text-sm font-medium transition-colors sm:px-4 lg:py-2
                                    ${tab === item.key
                                        ? 'border-brand-600 text-brand-800'
                                        : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                    >
                        <item.icon className="h-4 w-4" /> {item.label}
                    </button>
                ))}
            </div>

            <DataTable
                className="min-h-0 flex-1"
                columns={columns}
                rows={rows || []}
                loading={loading}
                rowKey={(row, index) => row.Supplier_ID ?? row.Customer_ID ?? row.Cash_Movement_ID ?? index}
                emptyTitle={
                    tab === 'payables' ? 'Nothing owed to suppliers'
                        : tab === 'receivables' ? 'No customer owes anything'
                            : 'No cash movements recorded'
                }
                emptyDescription={
                    tab === 'cash'
                        ? 'Money taken out of or put into the drawer outside a sale appears here.'
                        : 'Balances build up from unpaid purchases and credit sales.'
                }
                emptyIcon={tab === 'cash' ? FiDollarSign : FiTruck}
            />

            <SettleDialog
                settling={settling}
                onClose={() => setSettling(null)}
                onSaved={refreshAll}
            />
        </div>
    );
};

/* ----------------------------- record a payment --------------------------- */

const SettleDialog = ({ settling, onClose, onSaved }) => {
    const [amount, setAmount] = useState('');
    const [reason, setReason] = useState('');
    const [isCash, setIsCash] = useState(true);

    const isSupplier = settling?.kind === 'supplier';
    const row = settling?.row;
    const balance = Number(row?.Current_Balance) || 0;

    const { run: save, busy } = useAction(
        () => (isSupplier
            ? accountsApi.paySupplier({
                supplierId: row.Supplier_ID,
                amount: Number(amount),
                reason: reason.trim() || null,
                isCash
            })
            : accountsApi.receiveFromCustomer({
                customerId: row.Customer_ID,
                amount: Number(amount),
                reason: reason.trim() || null
            })),
        {
            onSuccess: () => {
                setAmount(''); setReason(''); setIsCash(true);
                onSaved(); onClose();
            }
        }
    );

    const value = Number(amount) || 0;
    const overpaying = value > balance;

    return (
        <Modal
            open={!!settling}
            onClose={onClose}
            title={isSupplier
                ? `Pay ${row?.Supplier_Name || ''}`
                : `Receive from ${row?.Customer_Name || ''}`}
            subtitle={`Outstanding Rs. ${money(balance)}`}
            width="sm"
            dismissible={!busy}
            footer={(
                <>
                    <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={() => save()}
                        disabled={busy || value <= 0}
                    >
                        {busy ? 'Saving…' : `Record Rs. ${money(value)}`}
                    </button>
                </>
            )}
        >
            <div className="space-y-3">
                <Field
                    label="Amount"
                    required
                    error={overpaying ? 'That is more than the outstanding balance.' : null}
                    hint={!overpaying ? 'Part payments are fine - the balance carries forward.' : null}
                >
                    <NumberInput
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        data-autofocus
                    />
                </Field>

                <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={() => setAmount(String(balance))}
                >
                    Settle in full ({money(balance)})
                </button>

                <Field label="Note">
                    <Input
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder={isSupplier ? 'Cheque no., or which invoices' : 'What this covers'}
                    />
                </Field>

                {isSupplier && (
                    <Checkbox
                        label="Paid in cash from the drawer"
                        description="Records the outflow against the open shift, so the drawer still reconciles at close."
                        checked={isCash}
                        onChange={setIsCash}
                    />
                )}

                {!isSupplier && (
                    <p className="rounded-lg bg-slate-50 p-2.5 text-xs text-slate-600">
                        Recorded as cash into the drawer against the open shift. If no shift is
                        open the balance is still cleared, but nothing is added to the drawer.
                    </p>
                )}
            </div>
        </Modal>
    );
};

export default AccountsPage;
